/**
 * Integrações e API — §5.14 da spec 04.
 *
 * ## Os dois segredos desta tela, e por que são diferentes
 *
 * **Chave de API**: guardada como SHA-256, porque o Hugi só **verifica** o que o cliente manda.
 * **Segredo de webhook**: guardado em claro, porque o Hugi **assina** com ele — e não existe hash que
 * permita produzir HMAC.
 *
 * Os dois aparecem **uma vez**. A tela precisa deixar isso óbvio antes de a pessoa fechar o modal, e é
 * por isso que o aviso fica junto do valor, e não num rodapé.
 *
 * ## O verificador de referência fica na tela
 *
 * §5.14: *"a tela mostra o verificador de referência para o cliente colar no código dele"*. É o que
 * separa um webhook assinado que ninguém verifica de um que é verificado — e a maioria dos clientes não
 * escreveria essa função sozinha.
 *
 * ## Endpoint desativado por falha aparece com o motivo
 *
 * *"Fila de webhook crescendo em silêncio é o que transforma indisponibilidade do cliente em problema
 * nosso."* Então: banner, o motivo por escrito, e o log de tentativas ao alcance de um clique.
 */

import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { chamar, falhaDe, useSessao, type LinhaDaListagem } from "@hugi/ui";
import { Carregando, GradeDeCartoes, PaginaInteira, SemPermissao, Selo, type CartaoDaGrade } from "@hugi/ui/kit";
import {
  useChaves,
  useEntregas,
  useWebhooks,
  type ChaveDeApi,
  type WebhookDeSaida,
} from "./dados.js";
import { CampoComDica, ListaDaAba, NadaEscolhido, Widgets, type PainelDaAba, type PartesDaAba, type PropsDaAba } from "./Widgets.js";

/** O que cada escopo libera, em português. Quem cria a chave raramente é quem vai usá-la (§5.14). */
const ESCOPOS: readonly { id: string; rotulo: string; explicacao: string }[] = [
  { id: "read_conversation", rotulo: "Ler conversas", explicacao: "Listar atendimentos e ler o histórico de mensagens." },
  { id: "respond_conversation", rotulo: "Responder", explicacao: "Assumir atendimento e enviar mensagem ao cliente." },
  { id: "manage_contact", rotulo: "Gerenciar contatos", explicacao: "Editar contato e aplicar etiqueta." },
  { id: "transfer_conversation", rotulo: "Transferir", explicacao: "Passar atendimento para outra fila ou pessoa." },
  { id: "resolve_conversation", rotulo: "Finalizar", explicacao: "Encerrar atendimento com motivo." },
  { id: "comment_conversation", rotulo: "Deixar nota interna", explicacao: "Registrar na conversa algo que o cliente não vê." },
  { id: "read_team_conversations", rotulo: "Ver da equipe inteira", explicacao: "Sem este escopo, a chave não vê atendimento nenhum — 'os meus' não significa nada para uma máquina." },
  { id: "read_conversation_history", rotulo: "Ler conversas encerradas", explicacao: "Sem ele, pedir `estado=finalizado` devolve vazio — não erro." },
  { id: "send_campaign", rotulo: "Disparar campanhas", explicacao: "Agendar, pausar, retomar e cancelar campanhas de template para uma lista com opt-in. É fatura da Meta e franquia do plano — dê só a quem decide isso." },
  { id: "read_channel", rotulo: "Ver os canais", explicacao: "Listar os números que o Hugi atende por você, e o estado de cada um." },
  { id: "read_report", rotulo: "Ler relatórios", explicacao: "Os números de volume, origem e consumo, e quanto do plano já foi usado." },
  { id: "connect_channel", rotulo: "Conectar um número", explicacao: "Pedir o link da Meta para ligar mais um WhatsApp, sem passar por esta tela." },
  { id: "manage_catalog", rotulo: "Gerenciar catálogos", explicacao: "Filas, motivos, etapas do funil e o catálogo de produtos e serviços — o que compõe o card e o que o fecha." },
  { id: "manage_api_key", rotulo: "Gerenciar chaves", explicacao: "Criar e revogar OUTRAS chaves. Serve para rotacionar sem depender de alguém logar aqui — e nunca dá mais escopo do que a chave que criou." },
];

/**
 * O que o Hugi manda, em português — e é a lista que a tela oferece.
 *
 * ⚠️ **Os nomes têm de bater com `EVENTOS_DE_INTEGRACAO` do servidor.** O servidor recusa evento
 * desconhecido com o nome errado na resposta, então uma divergência aqui aparece como erro na tela e
 * não como endpoint inscrito em nada — que era o modo de falha silencioso.
 */
type Aba = "botoes" | "webhooks" | "chaves";
const ABAS: readonly { id: Aba; rotulo: string }[] = [
  { id: "botoes", rotulo: "Botões do site" },
  { id: "webhooks", rotulo: "Webhooks" },
  { id: "chaves", rotulo: "Chaves de API" },
];

const EVENTOS: readonly { id: string; rotulo: string; explicacao: string }[] = [
  {
    id: "mensagem.recebida",
    rotulo: "Mensagem recebida",
    explicacao: "O cliente escreveu. Vem com o contato, o atendimento, o protocolo e — quando há anexo — o endereço para baixar a mídia.",
  },
  {
    id: "mensagem.recibo",
    rotulo: "Recibo de envio",
    explicacao: "O que você enviou foi aceito, entregue, lido ou falhou.",
  },
  {
    id: "mensagem.transcrita",
    rotulo: "Áudio transcrito",
    explicacao: "O texto de um áudio, que sai depois da mensagem porque a transcrição é assíncrona. Vem com `transcricao: null` quando não foi possível transcrever.",
  },
  {
    id: "atendimento.aberto",
    rotulo: "Conversa aberta",
    explicacao: "Uma conversa nova entrou na fila.",
  },
  {
    id: "atendimento.finalizado",
    rotulo: "Conversa encerrada",
    explicacao: "Com motivo, e com os dois tempos separados: espera e atendimento.",
  },
  {
    id: "plano.limite",
    rotulo: "Franquia do mês",
    explicacao:
      "Você usou 80% ou 100% das conversas incluídas no mês. Sai uma vez por limiar, e nada é " +
      "cortado — o que passa entra como excedente na fatura.",
  },
  {
    id: "campanha.pausada",
    rotulo: "Campanha pausada",
    explicacao:
      "A campanha parou — sozinha, porque o template caiu de qualidade ou foi reclassificado pela Meta, " +
      "ou porque alguém pausou. Retomar é decisão de gente.",
  },
  {
    id: "campanha.concluida",
    rotulo: "Campanha concluída",
    explicacao: "Todos os destinatários tiveram desfecho. As contagens estão em GET /campanhas/{id}.",
  },
  {
    id: "template.bloqueado",
    rotulo: "Template bloqueado",
    explicacao:
      "A Meta reclassificou, pausou ou desativou um template, e ele ficou bloqueado no Hugi até alguém liberar em Canais.",
  },
  {
    id: "oportunidade.criada",
    rotulo: "Oportunidade criada",
    explicacao: "Um lead entrou no funil — sozinho, quando alguém responde a uma campanha, ou pela mão de alguém.",
  },
  {
    id: "oportunidade.movida",
    rotulo: "Oportunidade movida",
    explicacao: "O card mudou de etapa.",
  },
  {
    id: "oportunidade.fechada",
    rotulo: "Oportunidade fechada",
    explicacao: "Ganha ou perdida (com o motivo). O CRM de quem integra fecha o ciclo com este evento.",
  },
];


/**
 * A TELA (spec 22): Consulta. Um cartão com as três listas como ABAS (botões, webhooks, chaves) e,
 * ao lado, o painel do escolhido ou do que se cria — a mesma forma nas três (regra do owner de
 * 2026-09-03: abas em vez de três colunas; com 40 chaves, três colunas viravam três rolagens). A
 * página não rola: a tabela e o painel rolam por dentro, e as ações do painel ficam no rodapé dele.
 * No celular, um cartão por vez.
 */
export function Integracoes() {
  const sessao = useSessao();
  // A aba vem da URL quando houver (`#chaves`): "olha essa chave" vira um link.
  const daUrl = typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
  const [aba, setAba] = useState<Aba>(ABAS.some((a) => a.id === daUrl) ? (daUrl as Aba) : "botoes");
  const [noCelular, setNoCelular] = useState("listas");
  if (!sessao.permissoes.includes("manage_integration")) {
    return (
      <PaginaInteira rotulo="Integrações e API">
        <div className="v2-cartao">
          <SemPermissao>
            Esta tela exige a permissão <code>manage_integration</code>. Um administrador da sua empresa concede.
          </SemPermissao>
        </div>
      </PaginaInteira>
    );
  }

  const montar = (partes: PartesDaAba): ReactNode => {
    const cartoes: CartaoDaGrade[] = [
      {
        id: "listas",
        titulo: "Integrações e API",
        rotuloCurto: "Lista",
        area: "listas",
        dica: "Botões para o site, webhooks para avisar o seu sistema, chaves para ele chamar o Hugi.",
        tituloComAbas: true,
        aba,
        aoTrocarAba: (id) => setAba(id as Aba),
        abas: ABAS.map((a) => ({ id: a.id, rotulo: a.rotulo, conteudo: () => partes.lista })),
      },
      {
        id: "painel",
        titulo: partes.painel.titulo,
        rotuloCurto: "Detalhe",
        area: "painel",
        conteudo: partes.painel.conteudo,
        ...(partes.painel.rodape ? { rodape: partes.painel.rodape } : {}),
      },
    ];
    return (
      <PaginaInteira rotulo="Integrações e API" classe="hugi-integracoes">
        <GradeDeCartoes
          rotulo="Integrações e API"
          cartoes={cartoes}
          areas={'"listas painel"'}
          colunas="minmax(0, 3fr) minmax(0, 2fr)"
          linhas="minmax(0, 1fr)"
          ativo={noCelular}
          aoTrocar={setNoCelular}
        />
      </PaginaInteira>
    );
  };
  const propsDaAba: PropsDaAba = { montar, abrirPainel: () => setNoCelular("painel") };

  // `key`: cada aba é um cadastro próprio — trocar de aba não leva o escolhido de uma para a outra.
  return aba === "botoes" ? (
    <Widgets key="botoes" {...propsDaAba} />
  ) : aba === "webhooks" ? (
    <Webhooks key="webhooks" {...propsDaAba} />
  ) : (
    <Chaves key="chaves" {...propsDaAba} />
  );
}

/** O segredo recém-criado: aparece uma vez, e o aviso fica junto do valor. */
function segredoNovo(rotulo: string, valor: string, aoFechar: () => void): PainelDaAba {
  return {
    titulo: rotulo,
    conteudo: (
      <div className="hugi-integracoes-detalhe">
        {/*
          `break-all` e fonte mono: é um valor de 64 caracteres que a pessoa vai copiar. Quebrar no meio de
          uma palavra é o comportamento certo aqui.
        */}
        <code className="hugi-integracoes-segredo">{valor}</code>
        <p className="hugi-integracoes-aviso">
          Copie agora. Este valor <strong>não aparece de novo</strong> — nem para você, nem para o suporte.
        </p>
      </div>
    ),
    rodape: (
      <div className="hugi-integracoes-acoes">
        <button
          type="button"
          className="v2-botao"
          onClick={() => {
            void navigator.clipboard?.writeText(valor);
          }}
        >
          Copiar
        </button>
        <button type="button" className="v2-botao is-primario" onClick={aoFechar}>
          Já copiei
        </button>
      </div>
    ),
  };
}

/* ============================== Webhooks ============================== */

function linhaDoWebhook(w: WebhookDeSaida): LinhaDaListagem {
  const estado = w.desativadoEm
    ? { rotulo: "Desativado", tom: "ruim" as const }
    : w.falhandoDesde
      ? { rotulo: "Falhando", tom: "degradado" as const }
      : { rotulo: "Ativo", tom: "ok" as const };
  return {
    id: w.id,
    nome: w.url.replace(/^https?:\/\//, ""),
    tipo: w.eventos.length === 0 ? "Todos os eventos" : `${w.eventos.length} evento(s)`,
    estado,
    criadoEm: w.criadoEm,
    fimEm: w.desativadoEm,
    busca: w.url,
  };
}

function Webhooks(props: PropsDaAba) {
  const cliente = useQueryClient();
  const webhooks = useWebhooks();
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [cadastrando, setCadastrando] = useState(false);
  const [url, setUrl] = useState("");
  const [escolhidos, setEscolhidos] = useState<readonly string[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [segredo, setSegredo] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const alternar = (id: string) =>
    setEscolhidos((atual) => (atual.includes(id) ? atual.filter((e) => e !== id) : [...atual, id]));

  const criar = async () => {
    setErro(null);
    setCriando(true);
    try {
      const r = await chamar<{ id: string; segredo: string }>("/api/painel/webhooks", {
        metodo: "POST",
        corpo: { url, eventos: escolhidos },
      });
      setSegredo(r.segredo);
      setUrl("");
      setEscolhidos([]);
      setCadastrando(false);
      await cliente.invalidateQueries({ queryKey: ["webhooks"] });
    } catch (e) {
      setErro(falhaDe(e)?.erro.mensagem ?? "Não foi possível cadastrar.");
    } finally {
      setCriando(false);
    }
  };
  const remover = async (id: string) => {
    await chamar(`/api/painel/webhooks/${id}`, { metodo: "DELETE" }).catch(() => null);
    setEscolhido(null);
    await cliente.invalidateQueries({ queryKey: ["webhooks"] });
  };

  const lista = webhooks.data?.webhooks ?? [];
  const w = lista.find((x) => x.id === escolhido) ?? null;

  let painel: PainelDaAba;
  if (segredo) {
    painel = segredoNovo("O segredo deste endpoint", segredo, () => setSegredo(null));
  } else if (cadastrando) {
    const semEndereco = criando || !url.trim();
    painel = {
      titulo: "Novo endpoint",
      conteudo: (
        <div className="hugi-integracoes-form">
          <CampoComDica rotulo="Endereço" largo>
            {(p) => (
              <input {...p} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://seu-sistema.com.br/webhooks/hugi" />
            )}
          </CampoComDica>
          <fieldset className="hugi-integracoes-marcar is-largo">
            <legend>Eventos — nenhum marcado é todos</legend>
            {EVENTOS.map((e) => (
              <label key={e.id} title={e.explicacao}>
                <input type="checkbox" checked={escolhidos.includes(e.id)} onChange={() => alternar(e.id)} />
                <span>
                  <span className="hugi-integracoes-forte">{e.rotulo}</span> <code>{e.id}</code>
                </span>
              </label>
            ))}
          </fieldset>
          <div className="is-largo">
            <Verificador />
          </div>
        </div>
      ),
      rodape: (
        <div className="hugi-integracoes-acoes">
          {erro ? (
            <p role="alert" className="v2-erro">
              {erro}
            </p>
          ) : null}
          <button type="button" className="v2-botao" onClick={() => setCadastrando(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="v2-botao is-primario"
            onClick={() => void criar()}
            disabled={semEndereco}
            title={semEndereco ? "Escreva o endereço." : undefined}
          >
            {criando ? "Cadastrando…" : "Cadastrar"}
          </button>
        </div>
      ),
    };
  } else if (w) {
    painel = {
      titulo: w.url.replace(/^https?:\/\//, ""),
      conteudo: (
        <div className="hugi-integracoes-detalhe">
          <p className="hugi-integracoes-segredo">{w.url}</p>
          <p className="hugi-integracoes-mudo">
            {w.eventos.length === 0 ? "Recebe todos os eventos." : w.eventos.join(", ")}
          </p>
          {w.desativadoEm ? (
            <p className="hugi-integracoes-erro">
              Desativado em {new Date(w.desativadoEm).toLocaleString("pt-BR")}
              {w.desativadoMotivo ? ` — ${w.desativadoMotivo}` : ""}
            </p>
          ) : w.falhandoDesde ? (
            <p className="hugi-integracoes-aviso">Falhando desde {new Date(w.falhandoDesde).toLocaleString("pt-BR")}.</p>
          ) : null}
          <Entregas webhookId={w.id} />
        </div>
      ),
      rodape: (
        <div className="hugi-integracoes-acoes">
          <button type="button" className="v2-botao is-perigo" onClick={() => void remover(w.id)}>
            Remover
          </button>
        </div>
      ),
    };
  } else {
    painel = { titulo: "Endpoint", conteudo: <NadaEscolhido>Escolha um endpoint na lista, ou cadastre um.</NadaEscolhido> };
  }

  return props.montar({
    lista: (
      <ListaDaAba
        titulo="Webhooks"
        memoria="hugi:integracoes:webhooks"
        linhas={webhooks.data ? lista.map(linhaDoWebhook) : undefined}
        carregando={webhooks.isPending}
        erro={webhooks.error}
        aoTentarDeNovo={() => void webhooks.refetch()}
        escolhido={escolhido}
        aoEscolher={(id) => {
          setEscolhido(id);
          setCadastrando(false);
          props.abrirPainel();
        }}
        vazio="Nenhum endpoint. Sem ele, o seu sistema não fica sabendo dos recibos."
        contagem={(n) => (n === 1 ? "1 endpoint" : `${n} endpoints`)}
        acoes={
          <button
            type="button"
            className="v2-botao is-primario is-pequeno"
            onClick={() => {
              setCadastrando(true);
              setEscolhido(null);
              props.abrirPainel();
            }}
          >
            Cadastrar endpoint
          </button>
        }
      />
    ),
    painel,
  });
}

function Entregas(props: { readonly webhookId: string }) {
  const q = useEntregas(props.webhookId);
  if (q.isPending) return <Carregando>Carregando as tentativas…</Carregando>;
  if (q.error) return <p className="hugi-integracoes-erro">Não foi possível carregar as tentativas.</p>;

  const entregas = q.data!.entregas;
  if (entregas.length === 0) {
    return (
      <p className="hugi-integracoes-mudo">
        Nenhuma tentativa ainda. A primeira aparece assim que houver um recibo para entregar.
      </p>
    );
  }

  return (
    <div className="hugi-integracoes-tentativas" role="region" aria-label="Tentativas de entrega" tabIndex={0}>
      <table className="v2-tabela is-compacta">
        <thead>
          <tr>
            <th scope="col">Quando</th>
            <th scope="col">Evento</th>
            <th scope="col" className="is-num">Tentativa</th>
            <th scope="col">Resultado</th>
            <th scope="col" className="is-num">Latência</th>
          </tr>
        </thead>
        <tbody>
          {entregas.map((e) => (
            <tr key={e.id}>
              <td>{new Date(e.criadaEm).toLocaleString("pt-BR")}</td>
              <td className="hugi-integracoes-mono">{e.evento}</td>
              <td className="is-num">{e.tentativa}</td>
              <td>
                {e.entregueEm ? (
                  <Selo tom="sucesso">{String(e.statusHttp ?? 200)}</Selo>
                ) : (
                  <Selo tom="perigo">{e.erro ?? "pendente"}</Selo>
                )}
              </td>
              {/*
                A latência é o que revela endpoint lento antes de ele virar endpoint morto — e é o que a
                spec pede junto do status.
              */}
              <td className="is-num">{e.duracaoMs === null ? "—" : `${e.duracaoMs} ms`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** O verificador de referência, para o cliente colar no código dele (§5.14). */
function Verificador() {
  const [aberto, setAberto] = useState(false);
  const codigo = `import { createHmac, timingSafeEqual } from "node:crypto";

// Confira ANTES de processar o corpo. E use o corpo CRU: reserializar muda bytes.
export function assinaturaDoHugiConfere(corpoCru, cabecalho, segredo) {
  if (!cabecalho) return false;
  const esperada = "sha256=" + createHmac("sha256", segredo).update(corpoCru).digest("hex");
  const a = Buffer.from(esperada);
  const b = Buffer.from(cabecalho);
  // Comprimento antes: timingSafeEqual LANÇA com tamanhos diferentes.
  return a.length === b.length && timingSafeEqual(a, b);
}`;

  return (
    <div>
      <button type="button" onClick={() => setAberto((v) => !v)} aria-expanded={aberto} className="v2-link">
        {aberto ? "Ocultar" : "Como verificar a assinatura no seu código"}
      </button>
      {aberto ? (
        <div className="hugi-integracoes-detalhe">
          <pre className="hugi-integracoes-codigo">
            <code>{codigo}</code>
          </pre>
          <p className="hugi-integracoes-mudo">
            O cabeçalho é <code>x-hugi-signature-256</code>. Hoje a assinatura não carrega timestamp, então
            um payload capturado é reenviável — o que protege é a sua idempotência: derive o id do evento
            de <code>externalMessageId</code> + <code>status</code> e ignore repetido.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/* ============================== Chaves de API ============================== */

function linhaDaChave(c: ChaveDeApi): LinhaDaListagem {
  return {
    id: c.id,
    nome: c.nome,
    tipo: c.escopos.every((e) => e.endsWith(":ler")) ? "Só leitura" : "Leitura e escrita",
    estado: c.revogadaEm ? { rotulo: "Revogada", tom: "ruim" } : { rotulo: "Ativa", tom: "ok" },
    criadoEm: c.criadaEm,
    fimEm: c.revogadaEm,
    busca: c.prefixo,
  };
}

function Chaves(props: PropsDaAba) {
  const cliente = useQueryClient();
  const chaves = useChaves();
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [criandoChave, setCriandoChave] = useState(false);
  const [nome, setNome] = useState("");
  const [escolhidos, setEscolhidos] = useState<string[]>(["read_conversation"]);
  const [segredo, setSegredo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const criar = async () => {
    setErro(null);
    try {
      const r = await chamar<{ segredo: string }>("/api/painel/chaves", {
        metodo: "POST",
        corpo: { nome, escopos: escolhidos },
      });
      setSegredo(r.segredo);
      setNome("");
      setCriandoChave(false);
      await cliente.invalidateQueries({ queryKey: ["chaves"] });
    } catch (e) {
      setErro(falhaDe(e)?.erro.mensagem ?? "Não foi possível criar a chave.");
    }
  };
  const revogar = async (id: string) => {
    await chamar(`/api/painel/chaves/${id}`, { metodo: "DELETE" }).catch(() => null);
    await cliente.invalidateQueries({ queryKey: ["chaves"] });
  };

  const lista = chaves.data?.chaves ?? [];
  const c = lista.find((x) => x.id === escolhido) ?? null;

  let painel: PainelDaAba;
  if (segredo) {
    painel = segredoNovo("A sua chave", segredo, () => setSegredo(null));
  } else if (criandoChave) {
    const semMotivo = !nome.trim() || escolhidos.length === 0;
    painel = {
      titulo: "Nova chave",
      conteudo: (
        <div className="hugi-integracoes-form">
          <CampoComDica rotulo="Nome" largo>
            {(p) => <input {...p} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: ERP da recepção" />}
          </CampoComDica>
          <fieldset className="hugi-integracoes-marcar is-largo is-duas">
            <legend>O que ela pode fazer</legend>
            {ESCOPOS.map((e) => (
              <label key={e.id} title={e.explicacao}>
                <input
                  type="checkbox"
                  checked={escolhidos.includes(e.id)}
                  onChange={(ev) =>
                    setEscolhidos((antes) => (ev.target.checked ? [...antes, e.id] : antes.filter((x) => x !== e.id)))
                  }
                />
                <span>{e.rotulo}</span>
              </label>
            ))}
          </fieldset>
        </div>
      ),
      rodape: (
        <div className="hugi-integracoes-acoes">
          {erro ? (
            <p role="alert" className="v2-erro">
              {erro}
            </p>
          ) : null}
          <button type="button" className="v2-botao" onClick={() => setCriandoChave(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="v2-botao is-primario"
            onClick={() => void criar()}
            disabled={semMotivo}
            title={semMotivo ? "Dê um nome e marque ao menos um escopo." : undefined}
          >
            Criar chave
          </button>
        </div>
      ),
    };
  } else if (c) {
    painel = {
      titulo: c.nome,
      conteudo: (
        <div className="hugi-integracoes-detalhe">
          {/* O prefixo é como se reconhece a chave depois — o segredo nunca mais aparece. */}
          <code className="hugi-integracoes-mudo">{c.prefixo}…</code>
          <p className="hugi-integracoes-escopos">
            {c.escopos.map((e) => (
              <span key={e} className="hugi-integracoes-escopo">
                {e}
              </span>
            ))}
          </p>
          <p className="hugi-integracoes-mudo">
            Criada em {new Date(c.criadaEm).toLocaleDateString("pt-BR")} ·{" "}
            {c.ultimoUsoEm ? `último uso ${new Date(c.ultimoUsoEm).toLocaleString("pt-BR")}` : "nunca usada"}
          </p>
          {c.revogadaEm ? (
            <p>
              <Selo tom="perigo">{`Revogada em ${new Date(c.revogadaEm).toLocaleDateString("pt-BR")}`}</Selo>
            </p>
          ) : null}
        </div>
      ),
      ...(c.revogadaEm
        ? {}
        : {
            rodape: (
              <div className="hugi-integracoes-acoes">
                <button type="button" className="v2-botao is-perigo" onClick={() => void revogar(c.id)}>
                  Revogar
                </button>
              </div>
            ),
          }),
    };
  } else {
    painel = { titulo: "Chave", conteudo: <NadaEscolhido>Escolha uma chave na lista, ou crie uma.</NadaEscolhido> };
  }

  return props.montar({
    lista: (
      <ListaDaAba
        titulo="Chaves de API"
        memoria="hugi:integracoes:chaves"
        linhas={chaves.data ? lista.map(linhaDaChave) : undefined}
        carregando={chaves.isPending}
        erro={chaves.error}
        aoTentarDeNovo={() => void chaves.refetch()}
        escolhido={escolhido}
        aoEscolher={(id) => {
          setEscolhido(id);
          setCriandoChave(false);
          props.abrirPainel();
        }}
        vazio="Nenhuma chave. Crie uma para o seu sistema chamar o Hugi."
        contagem={(n) => (n === 1 ? "1 chave" : `${n} chaves`)}
        acoes={
          <button
            type="button"
            className="v2-botao is-primario is-pequeno"
            onClick={() => {
              setCriandoChave(true);
              setEscolhido(null);
              props.abrirPainel();
            }}
          >
            Criar chave
          </button>
        }
      />
    ),
    painel,
  });
}
